import { useSenarioTemplateSet } from "components/scenarioManegement/ScenarioExpand/hook";
import { PageLevel, useConsoleState } from "providers/ConsoleStateProvider";
import { useLoadingState } from "providers/LoadingProvider";
import React from "react";
import { useHistory } from "react-router";
import {
  Interview,
  Mail,
  Monitor,
  MonitorAttribute,
  NotificationEvent,
} from "types/graphqlapi";
import { InterviewCanceledMapper, Status } from "types/status";
import { listInterviewsQuery } from "utils/apis/interview";
import { getMonitorByIdQuery } from "utils/apis/monitor";
import { fetchMonitorAssignByInterviewIdQuery } from "utils/apis/monitorAssign";
import { fetchMonitorAttributeByMonitorIdQuery } from "utils/apis/monitorAttribute";
import { isInterviewStarted } from "utils/common";

export type DisplayInterview = {
  status: Status;
  // ここ
  monitorList: (Monitor & {
    isMonitorLogin?: boolean;
    lastSentDate?: string;
  })[];
  monitorAttributeList: MonitorAttribute[][];
  monitorSentRequested: boolean;
} & Interview;

export const decideInterviewStatus = (interview: Interview): Status => {
  // キャンセルステータスに値がある場合、キャンセル
  if (interview.canceled !== 0)
    return InterviewCanceledMapper[interview.canceled] as Status;

  // 実施終了日が入っていれば実施済
  if (interview.actual_end_date) return "DONE";

  // 期間指定実査は予定日を過ぎているか、ワンタイム実査は実施開始日が入っていれば実施中
  if (isInterviewStarted(interview)) return "DOING";

  // 入室待ち
  // 入室済

  return "NOT_STARTED";
};

export const returnMonitorInfoByInterviewId = async (interviewId: string) => {
  const monitorAssignList = await fetchMonitorAssignByInterviewIdQuery(
    interviewId
  );

  const monitorList = (
    await Promise.all(
      monitorAssignList.map(async (monitorAssign) => {
        return {
          ...(await getMonitorByIdQuery(monitorAssign.monitor_id)),
          isMonitorLogin: monitorAssign.last_entry_date ? true : false,
        };
      })
    )
  ).sort((a, b) =>
    a.sort_index && b.sort_index
      ? a.sort_index > b.sort_index
        ? 1
        : -1
      : Number(a.ans_id) > Number(b.ans_id)
      ? 1
      : -1
  );
  const monitorAttributeList = await Promise.all(
    monitorAssignList.map(
      async (monitorAssign) =>
        await fetchMonitorAttributeByMonitorIdQuery(monitorAssign.monitor_id)
    )
  );
  return { monitorList, monitorAttributeList };
};

export const useInterviewList = (selectedSubjectId: string) => {
  const history = useHistory();
  const { setLoading } = useLoadingState();
  const { setScreenName, setPageLevel } = useConsoleState();
  const { fetchScenario } = useSenarioTemplateSet();
  const [displayInterviewList, setDisplayInterviewList] = React.useState<
    DisplayInterview[]
  >([]);

  const fetchInterviews = async () => {
    try {
      const queryResult = await listInterviewsQuery(selectedSubjectId);
      if (queryResult === undefined) return;

      const interviewWithStatusMonitorList = await Promise.all(
        queryResult.map(async (interview): Promise<DisplayInterview> => {
          const status = await decideInterviewStatus(interview);
          const monitorInfo = await returnMonitorInfoByInterviewId(
            interview.id
          );
          return {
            ...interview,
            status,
            monitorList: monitorInfo.monitorList,
            monitorAttributeList: monitorInfo.monitorAttributeList,
            monitorSentRequested: false,
          };
        })
      );

      const sortedList = interviewWithStatusMonitorList.sort((a, b) => {
        if (a.interview_seq < b.interview_seq) return -1;
        if (a.interview_seq > b.interview_seq) return 1;
        return 0;
      });
      setDisplayInterviewList(sortedList);
    } catch (e) {
      console.error(e);
      alert("エラーが発生しました。");
      setLoading(false);
      history.replace("/auth/login");
    }
  };

  React.useEffect(() => {
    setScreenName("案件詳細");
    setPageLevel(PageLevel.interviewManagement);
    fetchInterviews();

    window.history.replaceState({}, "", `/interview/${selectedSubjectId}`);
    fetchScenario();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { displayInterviewList, setDisplayInterviewList, fetchInterviews };
};
